// GSAP
import { gsap } from "gsap";
import { Flip } from "gsap/Flip";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText"
import { ScrollSmoother } from "gsap/ScrollSmoother";

gsap.registerPlugin(Flip, ScrollTrigger, SplitText, ScrollSmoother);

let mm = gsap.matchMedia();

// Tailwind
import resolveConfig from 'tailwindcss/resolveConfig'
import myConfig from '~/tailwind.config.js'

const tailwindConfig = resolveConfig(myConfig)

// =============================================================================
// General
// =============================================================================
export function generalAnimations() {
  mm.add("(min-width: 1023.98px)", () => {

    // ~~~~~~~~~~~ Animation Groups ~~~~~~~~~~ //
    let animGroups = document.querySelectorAll('[data-anim-group]')

    for (const animGroup of animGroups) {
      // Get all elems
      let animGroupElems = animGroup.querySelectorAll('[data-anim-elem]')
      /*
      let animTargets = []

      // Construct array
      for (const animGroupElem of animGroupElems) {
        animTargets.push(animGroupElem)
      }
      */

      // Set Defaults
      let animGroupStarts = 'top center'
      let animGroupEase = 'power2.out'
      let animGroupDelay = 0
      let animGroupMarkers = false
      let animGroupStagger = 0
      let animGroupDuration = 1

      // Get Group Data attributes
      if (animGroup.hasAttribute('data-anim-starts')) {
        animGroupStarts = animGroup.getAttribute('data-anim-starts')
      }
      if (animGroup.hasAttribute('data-anim-ease')) {
        animGroupEase = animGroup.getAttribute('data-anim-ease')
      }
      if (animGroup.hasAttribute('data-anim-delay')) {
        animGroupDelay = animGroup.getAttribute('data-anim-delay')
      }
      if (animGroup.hasAttribute('data-anim-markers')) {
        animGroupMarkers = true
      }
      if (animGroup.hasAttribute('data-anim-stagger')) {
        animGroupStagger = animGroup.getAttribute('data-anim-stagger')
      }
      if (animGroup.hasAttribute('data-anim-stagger')) {
        animGroupDuration = animGroup.getAttribute('data-anim-duration')
      }

      // Define Timeline
      let animGroupTL = gsap.timeline({
        defaults: {
          duration: animGroupDuration,
          delay: animGroupDelay,
          ease: animGroupEase
        },
        scrollTrigger: {
          trigger: animGroup,
          start: animGroupStarts,
          markers: animGroupMarkers
        }
      }).add('start')

      // -------- Effects ------- //

      // fadeInRight
      if (animGroup.getAttribute('data-anim-effect') == 'fadeInRight') {
        for (const [i, animGroupElem] of animGroupElems.entries()) {

          let offset = animGroupStagger * (i + 1)

          gsap.set(animGroupElem, {autoAlpha: 0, x: -50})
          animGroupTL.to(animGroupElem, {autoAlpha: 1, x: 0}, 'start+='+offset)  
        }
      }
      // fadeInLeft
      if (animGroup.getAttribute('data-anim-effect') == 'fadeInLeft') {
        for (const [i, animGroupElem] of animGroupElems.entries()) {

          let offset = animGroupStagger * (i + 1)

          gsap.set(animGroupElem, {autoAlpha: 0, x: 50})
          animGroupTL.to(animGroupElem, {autoAlpha: 1, x: 0}, 'start+='+offset)  
        }
      }

    }

    // ~~~~~~~~ Animation One-offs ~~~~~~~~ //
    let animElems = document.querySelectorAll('[data-anim-elem]')
    for (const animElem of animElems) {
      // Only want animElems that aren't part of a group
      if (!animElem.closest('[data-anim-group]')) return
    }
  })
}

// =============================================================================
// ScrollSmoother
// =============================================================================

// ScrollSmoother.create({
//   smooth: 0.3,
//   effects: true,
//   smoothTouch: 0.1,
// });

// =============================================================================
// Parallaxation
// =============================================================================
export function parallaxBg() {
  mm.add("(min-width: 1023.98px)", () => {
    let parallaxBg_elems = document.querySelectorAll('.parallax-bg');

    for (const parallaxBg_elem of parallaxBg_elems) {
      let parallaxBg_elem_styles = window.getComputedStyle(parallaxBg_elem),
        parallaxBg_image = parallaxBg_elem_styles.getPropertyValue('background-image'),
        parallaxBg_color = parallaxBg_elem_styles.getPropertyValue('background-color'),
        parallaxBg_size = parallaxBg_elem_styles.getPropertyValue('background-size'),
        parallaxBg_repeat = parallaxBg_elem_styles.getPropertyValue('background-repeat')
      ;
      
      parallaxBg_elem.style.background = 'none';
    
      let new_parallaxBg_div = document.createElement('div');
      new_parallaxBg_div.className = 'parallax-bg-elem'
      new_parallaxBg_div.style.backgroundImage = parallaxBg_image;
      new_parallaxBg_div.style.backgroundColor = parallaxBg_color;
      new_parallaxBg_div.style.backgroundSize = parallaxBg_size;
      new_parallaxBg_div.style.backgroundRepeat = parallaxBg_repeat;
      parallaxBg_elem.appendChild(new_parallaxBg_div);
    
      let parallaxBg_timeline = gsap.timeline({
        defaults: {
          duration: 1,
          ease: "linear"
        },
        scrollTrigger: {
          trigger: parallaxBg_elem,
          scrub: true,
        }
      });
    
      parallaxBg_timeline.to('.parallax-bg-elem', {duration: 1, yPercent: 20});
    } 
  })
}

// =============================================================================
// Hero Animation
// =============================================================================
export function heroAnimation() {
  const heroSection = document.querySelector('#hero-section')
  const logoPrimaries = gsap.utils.toArray('#logo svg .primary-color')
  const menuIcon = document.querySelector('.menu-icon')
  const menuItems = gsap.utils.toArray('#nav-primary li.menu-item')
  const headline = document.querySelector('#hero-headline')
  const subhead = document.querySelector('#hero-subhead')

  const heroMask = document.createElement('div')
  heroMask.id = 'hero-mask'
  heroMask.classList.add('bg-white', 'w-full', 'h-full', 'col-start-first', 'col-end-last', 'row-start-first', 'row-end-last', 'z-10')
  heroSection.appendChild(heroMask)

  gsap.set(logoPrimaries, {fill: tailwindConfig.theme.colors.blue.DEFAULT})
  gsap.set(menuItems, {color: tailwindConfig.theme.colors.blue.DEFAULT})
  gsap.set(subhead, {color: tailwindConfig.theme.colors.blue['300']})
  gsap.set(headline, {color: tailwindConfig.theme.colors.blue.DEFAULT})

  const heroAnimationTL = gsap.timeline({
    defaults: {
      duration: 1,
      delay: 2,
      ease: 'power2.out',
    }
  }).add('start')

  heroAnimationTL.to(heroMask, {yPercent: -100}, 'start')
  heroAnimationTL.to([subhead, headline, menuItems], {color: 'white'}, 'start')
  heroAnimationTL.to(logoPrimaries, {fill: 'white'}, 'start')

  let scrolled = document.scrollingElement.scrollTop;
  let position = document.querySelector('body').offsetTop;
  if( scrolled < position + 10 ) {
    menuIcon.classList.add('menu-icon--blue')

    setTimeout(() => {
      menuIcon.classList.remove('menu-icon--blue')
    }, 1000);
  }
}

// =============================================================================
// Home Animations
// =============================================================================
export function homeAnimations() {
    // ~~~~~~~~~~~~~~~~ About ~~~~~~~~~~~~~~~~ //
    const aboutSection = document.querySelector('#about-section')
    if (aboutSection) {
      const aboutHeadline = aboutSection.querySelector('#about-headline')
      const aboutSubhead = aboutSection.querySelector('#about-subhead')
      const aboutContent = aboutSection.querySelector('.content-block')

      if (aboutHeadline && aboutSubhead && aboutContent) {
        gsap.set([aboutHeadline, aboutSubhead, aboutContent], {autoAlpha: 0, x: -50})

        const aboutAnimationTL = gsap.timeline({
          defaults: {
            duration: 1,
            ease: 'power2.out'
          },
          scrollTrigger: {
            trigger: aboutSection,
            start: 'top 60%'
          }
        }).add('start')

        aboutAnimationTL.to([aboutHeadline, aboutSubhead, aboutContent], {autoAlpha: 1, x: 0, stagger: 0.15})
      }
    }

    // ~~~~~~~~~~~~~~~~ Services ~~~~~~~~~~~~~~~~ //
    const servicesSection = document.querySelector('#services-section')
    if (servicesSection) {
      const servicesHeadline = servicesSection.querySelector('#services-headline')
      const services = servicesSection.querySelectorAll('.service')
      
      if (servicesHeadline) {
        gsap.set(servicesHeadline, {autoAlpha: 0, x: -50})

        const servicesAnimationTL = gsap.timeline({
          defaults: {
            duration: 1,
            ease: 'power2.out'
          },
          scrollTrigger: {
            trigger: servicesSection,
            start: 'top 60%'
          }
        }).add('start')

        servicesAnimationTL.to(servicesHeadline, {autoAlpha: 1, x: 0, stagger: 0.15})
      }

      // Services (individual items)
      if (services.length > 0) {
        for (const service of services) {
          const serviceTitle = service.querySelector('.service-title')
          const serviceIcon = service.querySelector('.service-icon')
          const serviceLine = service.querySelector('.service-line')
          const serviceDot = service.querySelector('.service-dot')
          const serviceContent = service.querySelector('.content-block p')

          if (serviceTitle && serviceContent && serviceIcon && serviceLine && serviceDot) {
            // Sets
            gsap.set(serviceTitle, {autoAlpha: 0, x: -50})
            gsap.set(serviceContent, {autoAlpha: 0, x: -50})
            gsap.set(serviceIcon, {scale: 0})
            gsap.set(serviceLine, {height: 0})
            gsap.set(serviceDot, {scale: 0})

            const servicesAnimationTL = gsap.timeline({
              defaults: {
                duration: 1,
                ease: 'power2.out'
              },
              scrollTrigger: {
                trigger: service,
                start: 'top 60%',
              }
            }).add('start')

            servicesAnimationTL.to(serviceTitle, {autoAlpha: 1, x: 0}, 'start')
            servicesAnimationTL.to(serviceContent, {autoAlpha: 1, x: 0}, 'start+=0.15')
            servicesAnimationTL.to(serviceIcon, {scale: 1}, 'start')
            servicesAnimationTL.add('icon', 'start+=0.4')
            servicesAnimationTL.to(serviceLine, {height: '100%'}, 'icon')
            servicesAnimationTL.to(serviceDot, {scale: 1}, 'icon')
            servicesAnimationTL.add('content', 'icon-=0.4')
          }
        }
      }
    }

    // ~~~~~~~~~~~~~~~~ Team ~~~~~~~~~~~~~~~~ //
    const teamSection = document.querySelector('#team-section')
    if (teamSection) {
      const teamHeadline = teamSection.querySelector('#team-headline')
      const teamSubhead = teamSection.querySelector('#team-subhead')
      const teamContent = teamSection.querySelector('.content-block')

      if (teamHeadline && teamSubhead && teamContent) {
        gsap.set([teamHeadline, teamSubhead, teamContent], {autoAlpha: 0, x: -50})

        const teamAnimationTL = gsap.timeline({
          defaults: {
            duration: 1,
            ease: 'power2.out'
          },
          scrollTrigger: {
            trigger: teamSection,
            start: 'top 60%'
          }
        }).add('start')

        teamAnimationTL.to([teamHeadline, teamSubhead, teamContent], {autoAlpha: 1, x: 0, stagger: 0.15})
      }
    }

    // ~~~~~~~~~~~~~~~~ Contact ~~~~~~~~~~~~~~~~ //
    const contactSection = document.querySelector('#contact-section')
    if (contactSection) {
      const contactHeadline = contactSection.querySelector('#contact-headline')
      const contactSubhead = contactSection.querySelector('#contact-subhead')
      const contactForm = contactSection.querySelector('.form')

      if (contactHeadline && contactSubhead && contactForm) {
        gsap.set([contactHeadline, contactSubhead, contactForm], {autoAlpha: 0, y: 50})

        const contactAnimationTL = gsap.timeline({
          defaults: {
            duration: 1,
            ease: 'power2.out'
          },
          scrollTrigger: {
            trigger: contactSection,
            start: 'top 45%'
          }
        }).add('start')

        contactAnimationTL.to([contactHeadline, contactSubhead, contactForm], {autoAlpha: 1, y: 0, stagger: 0.15})
      }
    }

}

// =============================================================================
// Scroll Curve
// =============================================================================
export function scrollCurveAnimation() {

    const scrollCurveElem = document.querySelector('#scroll-curve')
    
    let scrollCurveAnimTL = gsap.timeline({
      scrollTrigger: {
        trigger: scrollCurveElem,
        start: 'top 70%',
        // end: (window.innerHeight / 2) +' top',
        toggleActions: "play pause resume reverse",
        // scrub: true,
      }
    }).add('start')

    scrollCurveAnimTL.to(scrollCurveElem, {duration: 0.5, ease: 'power1.out', scaleY: 1.2})
    scrollCurveAnimTL.to(scrollCurveElem, {duration: 2, ease: 'power4.out', scaleY: 0})

}

// =============================================================================
// Pull Star
// =============================================================================
export function pullStarAnimation() {

    const pullStarElems = document.querySelectorAll('.pull-star')

    for (const pullStarElem of pullStarElems) {
      const pullStarAnimationTL = gsap.timeline({
        defaults: {
          duration: 1,
          ease: 'power2.out'
        },
        scrollTrigger: {
          trigger: pullStarElem,
          start: 'top bottom',
          scrub: true,
        }
      }).add('start')

      pullStarAnimationTL.fromTo(pullStarElem, {y: 30}, {y: -30})
    }
}

// =============================================================================
// Background Elements
// =============================================================================
export function bgElemsAnimation() {
    const bgElemsElems = document.querySelectorAll('.bg-elem')

    for (const bgElemsElem of bgElemsElems) {
      const bgElemsAnimationTL = gsap.timeline({
        defaults: {
          duration: 1,
          ease: 'power2.out'
        },
        scrollTrigger: {
          trigger: bgElemsElem,
          start: 'top bottom',
          scrub: true,
        }
      }).add('start')

      bgElemsAnimationTL.fromTo(bgElemsElem, {y: '10%'}, {y: '-10%'})
    }

}

// =============================================================================
// Blog Feed Animation
// =============================================================================
export function blogFeedAnimation() {
  const feedItems = document.querySelectorAll('.blog-feed-item');

  if (feedItems.length === 0) return;

  feedItems.forEach((item, index) => {
    gsap.fromTo(
      item,
      {
        opacity: 0,
        y: 20
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: 'power2.out',
        delay: index * 0.1,
        scrollTrigger: {
          trigger: item,
          start: 'top 85%',
          toggleActions: 'play none none none'
        }
      }
    );
  });

  // Refresh ScrollTrigger to check initial viewport state
  ScrollTrigger.refresh();
}

// =============================================================================
// Single Post Content Animation
// =============================================================================
export function singlePostAnimation() {
  const heroContent = document.querySelector('.single-post-hero-content');
  const postTitle = document.querySelector('.single-post-title');
  const postMeta = document.querySelector('.single-post-meta');
  const postContent = document.querySelector('.single-post-content-inner');

  // Animate hero elements
  if (heroContent) {
    // Set initial state for hero elements
    if (postTitle) {
      gsap.set(postTitle, { opacity: 0, y: 30 });
    }
    if (postMeta) {
      gsap.set(postMeta, { opacity: 0, y: 20 });
    }

    // Animate hero title
    if (postTitle) {
      gsap.to(postTitle, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out',
        delay: 0.2
      });
    }

    // Animate hero meta
    if (postMeta) {
      gsap.to(postMeta, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out',
        delay: 0.4
      });
    }
  }

  // Animate content on page load
  if (postContent) {
    gsap.fromTo(
      postContent,
      {
        opacity: 0,
        y: 30
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out',
        delay: 0.6 // Delay after hero elements
      }
    );
  }
}